import { useCallback, useEffect, useMemo } from "react";
import { scrollToElement } from "../../lib/userFunctions";
import { Loading } from "../components/Loading";
import ErrorMessageBox from "app/components/ErrorMessageBox";
import LineBreak from "app/components/LineBreak";
import moment from "moment";
import { useQuery, useMutation } from "@tanstack/react-query";
import Api, { createFetchErrorMessage } from "utils/api/Api";
import { signoutAction } from "app/user/auth/ducks/auth";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

export const UserWarning = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    useEffect(() => {
        scrollToElement();
    }, []);

    const { data: unconfirmWarningList, isLoading, isError, error } = useQuery({
        queryKey: ['userWarningContainer'],
        queryFn: () => Api.get('/api/warning')
    });

    const confirmWarningMatate = useMutation({
        mutationFn: (id) => Api.get(`/api/warning/${id}/confirm`)
    });

    const onConfirmWarning = useCallback((id) => {
        if (confirmWarningMatate.isPending) return;
        if (!window.confirm("선택한 <주의>를 확인했습니까?")) return;
        confirmWarningMatate.mutate(id);
    }, [confirmWarningMatate]);

    const onLogout = useCallback(() => {
        if (!window.confirm('로그아웃 하시겠습니까?')) return;
        const result = dispatch(signoutAction());
        if (result) navigate(`/${window.schoolCode}/signin`);
    }, [dispatch, navigate]);

    const list = useMemo(() => {
        if (isLoading) return <Loading />;
        if (isError) return <li><ErrorMessageBox message={createFetchErrorMessage(error).message} /></li>;

        return unconfirmWarningList?.data?.list.map((item) => {
            return (
                <li key={item.id}>
                    <div>
                        <p className="rule">독서활동 제한 {item.restrictedDays}일 추가 </p>
                        <span className="date">{moment(item.createdAt).format("LLL")}</span>
                        <div className="content">
                            <LineBreak content={item.content} />

                            {item.confirmYn !== "Y" && (
                                <button className="btn _sm _blue" onClick={(e) => onConfirmWarning(item.id)} >
                                    확인
                                </button>
                            )}

                            {item.confirmYn === "Y" && (
                                <span className="date">
                                    &#60;주의&#62;확인: {moment(item.confirmDate).format("LLL")}
                                </span>
                            )}
                        </div>
                    </div>
                </li>
            );
        });
    }, [error, isError, isLoading, onConfirmWarning, unconfirmWarningList]);

    return (
        <div>
            <div className="userWarningContainer" id="userWarningContainer">
                <div className="warning_notice" data-aos="zoom-in-up" data-aos-duration="700" >

                    <div className="notice-box">
                        <h2 className="title">&#60;주의&#62; 를 받았습니다.</h2>
                        <div>
                            {unconfirmWarningList?.data?.restricted &&
                                moment().diff(moment(unconfirmWarningList?.restrictedAt), "seconds") < 0 && (
                                    <p>
                                        {moment(unconfirmWarningList?.data?.restrictedAt).format("LLL")}까지
                                        독서길라잡이 이용이 제한 됩니다.
                                    </p>
                                )}
                            {unconfirmWarningList?.data?.list.filter((item) => item.confirmYn !== "Y").length > 0 && (
                                <p>확인하지 않은 &#60;주의&#62;가 있습니다.</p>
                            )}
                        </div>
                        <div className="text-center">
                            <button className="btn _red" onClick={onLogout}><i className="fa-solid fa-right-from-bracket"></i> 로그아웃</button>
                        </div>
                    </div>
                </div>

                <div className="warning_list" data-aos="fade-zoom-in" data-aos-delay="500" data-aos-duration="1000">
                    <ul>{list}</ul>
                </div>

                <div className="warning_guide" data-aos="fade-zoom-in" data-aos-delay="500" data-aos-duration="1000">
                    <h2 className="sub_hdline">
                        <i className="fa-solid fa-circle-exclamation fs-30"></i> 독서길라잡이 &#60;주의&#62;
                        안내
                    </h2>
                    <div className="contents">
                        <div className="warningInfo">
                            <ul>
                                <li className="p-tb-1">
                                    <strong className="fs-18">
                                        1. 불성실한 독서 활동(낙서, 특정 문자로 도배, 반말 등)을 하는 어린이에게는 &#60;주의&#62;가 주어집니다.
                                    </strong>
                                    <ul className="p-tb-1 fs-16">
                                        <li>- 첫번째 &#60;주의&#62;를 받으면 1일 독서길라잡이 사용이 정지 됩니다.</li>
                                        <li>- 두번째 &#60;주의&#62; 부터는 7일 이상 독서길라잡이 사용이 정지 됩니다.</li>
                                    </ul>
                                </li>
                                <li className="p-tb-1">
                                    <strong className="fs-18">
                                        2. 성희롱, 욕설, 괴롭힘 등의 과도한 언어폭력은 30일 이상 독서길라잡이 사용이 정지 됩니다.
                                    </strong>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
